import { kebabCase } from '@fergdigitalcommerce/fergy-utilities';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, type HTMLAttributes, type ReactElement, useState, useEffect } from 'react';
import { type ProductOptionHelpFieldsFragment, type ProductOptionGroupFieldsFragment } from '../../../__generated__/graphql-client-types';
import { AnalyticsHelper } from '../../../helpers/analytics/analytics.helper';
import { buildGTMVariationGroup } from '../../../helpers/analytics/gtm/event-builders';
import { type CloudinaryOptions, generateCloudinaryImageProps } from '../../../helpers/cloudinary-helper/cloudinary-helper';
import { filterOptionsBy } from '../../../helpers/product-helper/configuration/product-configuration.helper';
import { type AddToCartAction } from '../../../helpers/product-helper/configuration/product-option.helper';
import { useClientOnlyBreakpoints } from '../../../hooks/breakpoints/client-only-breakpoints.hook';
import { type ProductOptionSelection } from '../../../types/product-option.types';
import { Modal } from '../../common-components/modal/modal.component';
import { Popover } from '../../common-components/popover/popover.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import HelpCircleIcon from '../../svgs/help-circle.svg';
import { ProductOptionsContent } from '../product-option/product-option.component';
import { ProductOptionCombined } from '../product-option-combined/product-option-combined.component';
import { ProductOptionInlineNested } from '../product-option-inline-nested/product-option-inline-nested.component';
import { ProductOptionRecommendedList } from '../product-option-recommended/product-option-recommended.component';
import { groupHelp } from './product-option-group.css';

const MAX_OPTION_COUNT = 10;
const MIN_OPTION_COUNT = 2;

export type ProductOptionGroupHelpProps = { inModal?: boolean } & ProductOptionHelpFieldsFragment &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Option Group Help Component
 */
export const ProductOptionGroupHelp: FunctionComponent<ProductOptionGroupHelpProps> = ({
	className = 'f5 ml2',
	title,
	description,
	image,
	inModal = false
}) => {
	const [isHelpOpen, setIsHelpOpen] = useState(false);
	const helpImageProps: CloudinaryOptions = { crop: 'limit', width: undefined, height: 400 };

	const triggerElement: ReactElement = <HelpCircleIcon className={`pointer ${className}`} />;
	const content: JSX.Element = (
		<div className={`flex flex-column pa2 f6 w-100 ${groupHelp}`}>
			{!inModal && <strong>{title}</strong>}
			{description && <p className="ma0 mt1" dangerouslySetInnerHTML={{ __html: description }} />}
			{image && <CloudinaryImage className="mw-none" {...generateCloudinaryImageProps(image, helpImageProps)} />}
		</div>
	);

	return inModal ? (
		<Modal size="fit" modalId={`${kebabCase(title)}`} title={title} triggerElement={triggerElement}>
			{content}
		</Modal>
	) : (
		<Popover toggleElement={triggerElement} isVisible={isHelpOpen} setIsVisible={setIsHelpOpen} direction="bottom">
			{content}
		</Popover>
	);
};

export type ProductOptionGroupProps = {
	groupPosition: number;
	itemClassName?: string;
	addToCart?: AddToCartAction;
	variantName?: string;
	isAddToCartBusy?: boolean;
	groupOptionCount?: number;
} & ProductOptionSelection &
	ProductOptionGroupFieldsFragment &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Option Group Component
 */
export const ProductOptionGroup: FunctionComponent<ProductOptionGroupProps> = ({
	itemClassName = 'w-100',
	addToCart,
	variantName,
	isAddToCartBusy,
	groupPosition,
	...props
}) => {
	const { isNotSmall } = useClientOnlyBreakpoints();
	const isRecommended = props.type === 'RECOMMENDED';
	const hasHeader = Boolean(props.label);
	const fontSize = 'f5-ns f6';

	const automationLabel = kebabCase(`product-option-group-${props.label}`).toString();

	useEffect(() => {
		if (props.type === 'VARIATION') {
			AnalyticsHelper.track(buildGTMVariationGroup(props.label, `${groupPosition}`)).catch(logError);
		}
	}, []);
	return (
		<li className="flex flex-column items-start justify-between w-100" data-automation={automationLabel}>
			{hasHeader && props.options.length > 0 && (
				<header className={'flex flex-row flex-nowrap items-center w-100 w-auto-ns mt3'} data-testid="product-group-header">
					<h3 className={`ma0 tc1-title w-90 w-auto-ns mr2 ${fontSize}`}>{props.label}</h3>
					{props.help && (
						<ProductOptionGroupHelp {...props.help} inModal={isRecommended && isNotSmall} className={`${fontSize}`} />
					)}
				</header>
			)}
			<div className="flex flex-column flex-nowrap items-start w-100" data-analytics={''}>
				<ProductOptionGroupFactoryOptions
					{...props}
					groupPosition={groupPosition}
					itemClassName={itemClassName}
					addToCart={addToCart}
					variantName={variantName}
					isAddToCartBusy={isAddToCartBusy}
				/>
			</div>
		</li>
	);
};

export type ProductOptionGroupFactoryOptionsProps = {
	groupPosition: number;
	itemClassName?: string;
	addToCart?: AddToCartAction;
	variantName?: string;
	isAddToCartBusy?: boolean;
	groupOptionCount?: number;
} & ProductOptionSelection &
	ProductOptionGroupFieldsFragment &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Group Options Factory of Product Options based on group type and selection type.
 */
export const ProductOptionGroupFactoryOptions: FunctionComponent<ProductOptionGroupFactoryOptionsProps> = ({
	itemClassName,
	addToCart,
	variantName,
	isAddToCartBusy,
	...props
}) => {
	const { selection, update, changeSelection, groupOptionCount, ...group } = props;
	const optionSelections = filterOptionsBy((groupSelection) => groupSelection.id === kebabCase(group.label), selection);
	if (props.type === 'RECOMMENDED') {
		// Recommended Product Options
		return (
			<ProductOptionRecommendedList
				{...props}
				itemClassName={itemClassName}
				className="w-100 pa0 mt3"
				selections={optionSelections}
				addToCart={addToCart}
				variantName={variantName}
				isAddToCartBusy={isAddToCartBusy}
				numOptionsToShow={groupOptionCount && groupOptionCount > MAX_OPTION_COUNT ? MIN_OPTION_COUNT : MAX_OPTION_COUNT}
			/>
		);
	} else if (props.selectionType === 'COMBINED') {
		// Combination of Associated products with Standard Product Options
		return <ProductOptionCombined {...group} className="w-100 pa0 mt2" selections={optionSelections} update={update} />;
	} else if (props.selectionType === 'NESTED') {
		return (
			<ProductOptionInlineNested
				{...props}
				optionSelections={optionSelections}
				update={update}
				selection={selection}
				changeSelection={changeSelection}
			/>
		);
	}
	// Standard Product Options
	return <ProductOptionsContent {...group} className="mt1" selections={optionSelections} update={update} />;
};

export type ProductOptionGroupListProps = {
	list: ProductOptionGroupFieldsFragment[];
	listItemClassName?: string;
	addToCart?: AddToCartAction;
	variantName?: string;
	isAddToCartBusy?: boolean;
} & ProductOptionSelection &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Option Group List Component
 */
export const ProductOptionGroupList: FunctionComponent<ProductOptionGroupListProps> = ({
	className = 'w-100 pa0',
	list,
	listItemClassName,
	variantName,
	isAddToCartBusy,
	...props
}) => {
	const groupOptionCount = list ? list.reduce((acc, group) => acc + group.options.length, 0) : 0;

	return (
		<ul className={`list flex flex-column items-center justify-start ma0 ${className}`} data-testid="product-option-groups">
			{list.map((optionGroup, index) => (
				<ProductOptionGroup
					key={`${optionGroup.label}-${index}`}
					{...optionGroup}
					{...props}
					groupPosition={index + 1}
					className={listItemClassName}
					variantName={variantName}
					isAddToCartBusy={isAddToCartBusy}
					groupOptionCount={groupOptionCount}
				/>
			))}
		</ul>
	);
};
